<template>
  <div class="container-fluid py-4 dashboard-wrapper">
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h1 class="dashboard-title">Admin Dashboard</h1>
    </div>

    <!-- Top Stats Cards -->
    <div class="row">
      <div
        class="col-lg-3 hh col-md-4 col-6 mb-3"
        v-for="(card, index) in topStatsCards"
        :key="index"
      >
        <div :class="['card text-white shadow-sm stats-card', card.class]">
          <div
            class="card-body d-flex flex-column justify-content-between align-items-center p-2"
          >
            <!-- Title -->
            <p class="card-title mb-1 font-weight-bold stats-label">
              {{ card.title }}
            </p>

            <!-- Loading -->
            <div
              v-if="card.loading"
              class="spinner-border spinner-border-sm text-light mt-1"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>

            <!-- Normal cards -->
            <div v-else class="w-100">
              <h5 v-if="!card.isAppointments" class="mb-0 text-center stats-number">
                {{ card.value }}
              </h5>

              <div v-else class="appointments-breakdown">
                <h5 class="text-center stats-number">{{ card.value }}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div class="row mt-4">
      <div class="col-md-6 mb-4">
        <div class="card shadow-sm">
          <div class="card-header bg-white border-bottom">
            <h5 class="mb-0">Category wise Talents</h5>
          </div>
          <div class="card-body">
            <LineChartWrapper :categoryWiseTalents="categoryWiseTalents" />
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-4">
        <div class="card shadow-sm">
          <div class="card-header bg-white border-bottom">
            <h5 class="mb-0">Region wise Talents</h5>
          </div>
          <div class="card-body">
            <BarChartWrapper :regionWiseTalents="regionWiseTalents" />
          </div>
        </div>
      </div>
    </div>

    <!-- User Section -->
    <div class="user-section mt-5">
      <h4 class="section-title mb-3">Select a User</h4>

      <!-- Custom searchable dropdown -->
      <div
        class="dropdown-root mb-4"
        ref="dropdownRoot"
        @keydown="handleDropdownKeydown"
      >
        <!-- Trigger -->
        <div
          class="dropdown-trigger"
          :class="{ open: dropdownOpen }"
          tabindex="0"
          @click.stop="toggleDropdown"
          @keyup.enter.prevent="toggleDropdown"
          @keyup.space.prevent="toggleDropdown"
        >
          <div class="trigger-text" :title="selectedUserDisplay">
            {{ selectedUserDisplay }}
          </div>
          <div class="trigger-icons">
            <button
              type="button"
              class="clear-btn"
              v-if="selectedUser"
              @click.stop="clearSelection"
              title="Clear selection"
            >
              ✕
            </button>
            <span class="chev" :class="{ rotated: dropdownOpen }" aria-hidden="true">▾</span>
          </div>
        </div>

        <!-- Menu -->
        <transition name="dropdown-fade">
          <div v-if="dropdownOpen" class="dropdown-panel" @click.stop>
            <!-- Search input inside dropdown -->
            <div class="dropdown-search-wrap">
              <input
                ref="searchInput"
                v-model="searchQuery"
                @input="onSearchInput"
                @keydown.down.prevent="focusNextItem"
                @keydown.up.prevent="focusPrevItem"
                @keydown.enter.prevent="chooseFocusedItem"
                type="text"
                class="dropdown-search"
                placeholder="Search by name, email or phone..."
                aria-label="Search users"
              />
              <div class="search-count" v-if="filteredUsers.length !== userList.length">
                {{ filteredUsers.length }} / {{ userList.length }}
              </div>
            </div>

            <div class="dropdown-list" ref="list" role="listbox" :aria-activedescendant="focusedId">
              <div
                v-for="(user, idx) in filteredUsers"
                :key="user.id"
                :id="'dd-item-' + user.id"
                class="dropdown-item"
                :class="{ focused: focusedIndex === idx }"
                role="option"
                @click.stop="selectUser(user)"
                @mouseenter="setFocusedIndex(idx)"
              >
                <div class="item-line item-name">{{ user.name || '—' }}</div>
                <div class="item-line item-meta">{{ user.email || '—' }} · {{ user.phone || '—' }}</div>
              </div>

              <div v-if="filteredUsers.length === 0" class="dropdown-empty">No users found</div>
            </div>

            <div class="dropdown-footer">
              <button class="btn btn-sm btn-secondary" @click="toggleDropdown">Close</button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Green Cards -->
      <div class="row">
        <div
          class="col-lg-3 col-md-4 col-6 mb-3"
          v-for="(card, index) in userStatsCards"
          :key="'user-' + index"
        >
          <div :class="['card text-white shadow-sm stats-card', card.class]">
            <div class="card-body d-flex flex-column justify-content-between align-items-center p-2">
              <p class="card-title mb-1 font-weight-bold stats-label">{{ card.title }}</p>

              <div
                v-if="card.loading"
                class="spinner-border spinner-border-sm text-light mt-1"
                role="status"
              >
                <span class="sr-only">Loading...</span>
              </div>

              <h5 v-else class="mb-0 text-center stats-number">{{ card.value }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Optional extra space for long pages -->
    <div style="height: 40px;"></div>
  </div>
</template>

<script>
import axios from "axios";
import LineChartWrapper from "@core/components/linechart/LineChart.vue";
import BarChartWrapper from "@core/components/barchart/BarChart.vue";

export default {
  name: "Dashboard",
  components: { LineChartWrapper, BarChartWrapper },
  data() {
    return {
      categoryWiseTalents: [],
      regionWiseTalents: [],
      userList: [],
      selectedUserId: "",
      selectedUser: null,
      searchQuery: "",
      dropdownOpen: false,
      focusedIndex: -1,
      error: null,

      topStatsCards: [
        { title: "Total Number of Users", value: "loading...", class: "stats-gradient", loading: true },
        { title: "Total Number of Talents", value: "loading...", class: "stats-gradient", loading: true },
        { title: "Total Number of Scouts", value: "loading...", class: "stats-gradient", loading: true },
        { title: "Total Number of Appointments", value: "loading...", class: "stats-gradient", loading: true, isAppointments: true, breakdown: { completed: 0, pending: 0, inProgress: 0 } },
        { title: "Total Number of Posts", value: "loading...", class: "stats-gradient", loading: true },
        { title: "Total Number of Reels", value: "loading...", class: "stats-gradient", loading: true },
        { title: "Geo Verse", value: "loading...", class: "stats-gradient", loading: true },
      ],

      userStatsCards: [
        { title: "Total Number of User Followers", value: "-", class: "stats-green", loading: false },
        { title: "Name of User", value: "-", class: "stats-green", loading: false },
        { title: "Role of User", value: "-", class: "stats-green", loading: false },
      ],
    };
  },

  computed: {
    filteredUsers() {
      if (!Array.isArray(this.userList)) return [];
      const q = (this.searchQuery || "").trim().toLowerCase();
      const sorted = this.userList.slice().sort((a, b) => {
        const A = (a.name || "").toLowerCase();
        const B = (b.name || "").toLowerCase();
        if (A < B) return -1;
        if (A > B) return 1;
        return 0;
      });

      if (!q) return sorted;
      return sorted.filter(u => {
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const phone = (u.phone || "").toString().toLowerCase();
        return name.includes(q) || email.includes(q) || phone.includes(q);
      });
    },

    selectedUserDisplay() {
      if (this.selectedUser) {
        return `${this.selectedUser.name || '—'} || ${this.selectedUser.email || '—'} || ${this.selectedUser.phone || '—'}`;
      }
      return "-- Choose a user --";
    },

    focusedId() {
      if (this.focusedIndex >= 0 && this.filteredUsers[this.focusedIndex]) {
        return "dd-item-" + this.filteredUsers[this.focusedIndex].id;
      }
      return null;
    },
  },

  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
          this.focusedIndex = this.filteredUsers.length ? 0 : -1;
        });
      } else {
        this.focusedIndex = -1;
      }
    },

    onSearchInput() {
      this.$nextTick(() => {
        this.focusedIndex = this.filteredUsers.length ? 0 : -1;
      });
    },

    selectUser(user) {
      if (!user) return;
      this.selectedUser = user;
      this.selectedUserId = user.id;
      this.dropdownOpen = false;
      this.searchQuery = "";
      this.focusedIndex = -1;
      this.onUserChange();
    },

    clearSelection() {
      this.selectedUser = null;
      this.selectedUserId = "";
      this.userStatsCards.forEach(c => (c.value = "-"));
    },

    focusNextItem() {
      if (!this.dropdownOpen) {
        this.toggleDropdown();
        return;
      }
      const len = this.filteredUsers.length;
      if (len === 0) return;
      this.focusedIndex = (this.focusedIndex + 1) % len;
      this.scrollFocusedIntoView();
    },

    focusPrevItem() {
      if (!this.dropdownOpen) {
        this.toggleDropdown();
        return;
      }
      const len = this.filteredUsers.length;
      if (len === 0) return;
      this.focusedIndex = (this.focusedIndex - 1 + len) % len;
      this.scrollFocusedIntoView();
    },

    setFocusedIndex(idx) {
      this.focusedIndex = idx;
    },

    chooseFocusedItem() {
      if (this.focusedIndex >= 0 && this.filteredUsers[this.focusedIndex]) {
        this.selectUser(this.filteredUsers[this.focusedIndex]);
      }
    },

    scrollFocusedIntoView() {
      this.$nextTick(() => {
        const list = this.$refs.list;
        const id = this.focusedId;
        if (!list || !id) return;
        const el = list.querySelector("#" + id);
        if (!el) return;
        const listRect = list.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        if (elRect.top < listRect.top) {
          list.scrollTop -= (listRect.top - elRect.top) + 8;
        } else if (elRect.bottom > listRect.bottom) {
          list.scrollTop += (elRect.bottom - listRect.bottom) + 8;
        }
      });
    },

    handleDropdownKeydown(e) {
      if (e.target && e.target.tagName === "INPUT") return;

      if (!this.dropdownOpen && (e.key === "ArrowDown" || e.key === "Down")) {
        this.toggleDropdown();
        return;
      }
      if (this.dropdownOpen) {
        if (e.key === "ArrowDown" || e.key === "Down") {
          this.focusNextItem();
        } else if (e.key === "ArrowUp" || e.key === "Up") {
          this.focusPrevItem();
        } else if (e.key === "Enter") {
          this.chooseFocusedItem();
        } else if (e.key === "Escape") {
          this.dropdownOpen = false;
          this.focusedIndex = -1;
        }
      }
    },

    handleClickOutside(e) {
      const root = this.$refs.dropdownRoot;
      if (!root) return;
      if (!root.contains(e.target)) {
        this.dropdownOpen = false;
        this.focusedIndex = -1;
      }
    },

    onUserChange() {
      if (!this.selectedUserId) {
        this.userStatsCards[0].value = "-";
        this.userStatsCards[1].value = "-";
        this.userStatsCards[2].value = "-";
        return;
      }
      this.fetchUserInfo();
      this.fetchFollowersCount();
    },

    // === API CALLS (kept as provided) ===
    fetchUsers() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Users/MyCoordinatedUsers", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          if (res.data.status === "success") {
            this.userList = res.data.data || [];
            this.topStatsCards[0].value = this.userList.length;
          } else {
            this.userList = [];
            this.topStatsCards[0].value = 0;
          }
        })
        .catch(err => {
          this.error = err?.message || "Failed to fetch users";
          this.userList = [];
          this.topStatsCards[0].value = 0;
        })
        .finally(() => {
          this.topStatsCards[0].loading = false;
        });
    },

    fetchTalents() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Users/LoadMyCoordinatedUsers?role=talent", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          this.topStatsCards[1].value = res.data.status === "success" ? (res.data.data?.length || 0) : 0;
        })
        .catch(() => (this.topStatsCards[1].value = 0))
        .finally(() => (this.topStatsCards[1].loading = false));
    },

    fetchScouts() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Users/LoadMyCoordinatedUsers?role=scout", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          this.topStatsCards[2].value = res.data.status === "success" ? (res.data.data?.length || 0) : 0;
        })
        .catch(() => (this.topStatsCards[2].value = 0))
        .finally(() => (this.topStatsCards[2].loading = false));
    },

    fetchAppointments() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Appointments/LoadDataCoordinator", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          const card = this.topStatsCards.find(c => c.isAppointments);
          if (!card) return;
          if (res.data.status === "success") {
            const appointments = res.data.data || [];
            card.value = appointments.length;
          } else {
            card.value = 0;
          }
        })
        .catch(() => {
          const card = this.topStatsCards.find(c => c.isAppointments);
          if (card) card.value = 0;
        })
        .finally(() => {
          const card = this.topStatsCards.find(c => c.isAppointments);
          if (card) card.loading = false;
        });
    },

    fetchPosts() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Posts/NewsFeedCoordinator", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          this.topStatsCards[4].value = res.data.status === "success" ? (res.data.data?.length || 0) : 0;
        })
        .catch(() => (this.topStatsCards[4].value = 0))
        .finally(() => (this.topStatsCards[4].loading = false));
    },

    fetchReels() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/PublicReel/LoadReelsCoordinator", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          this.topStatsCards[5].value = res.data.status === "success" ? (res.data.data?.length || 0) : 0;
        })
        .catch(() => (this.topStatsCards[5].value = 0))
        .finally(() => (this.topStatsCards[5].loading = false));
    },

    fetchAdvertisements() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Adverts", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          this.topStatsCards[6].value = res.data.status === "success" ? (res.data.data?.length || 0) : 0;
        })
        .catch(() => (this.topStatsCards[6].value = 0))
        .finally(() => (this.topStatsCards[6].loading = false));
    },

    fetchNews() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/News", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          this.topStatsCards[6].value = res.data.status === "success" ? (res.data.data?.length || 0) : 0;
        })
        .catch(() => (this.topStatsCards[6].value = 0))
        .finally(() => (this.topStatsCards[6].loading = false));
    },

    fetchCommunities() {
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Communities/LoadCommunitiesCoordinator", {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          if (this.topStatsCards[6]) {
            this.topStatsCards[6].value = res.data.status === "success" ? (res.data.data?.length || 0) : 0;
            this.topStatsCards[6].loading = false;
          }
        })
        .catch(() => {
          if (this.topStatsCards[6]) this.topStatsCards[8].value = 0;
        });
    },

  fetchTotalFollowersAnalytics() {
  const userId =this.$store.state.userData.userID;

  axios.get(
    `https://geotalent-backend-production.up.railway.app/api/UserProfile/GetFollowersAnalytics?userID=${userId}`,
    {
      headers: { 
        Authorization: "Bearer " + this.$store.state.userData.token 
      },
    }
  )
  .then(res => {
    if (res.data.status === "success") {
      this.categoryWiseTalents = res.data.categoryWiseTalents || [];
      this.regionWiseTalents = res.data.regionWiseTalents || [];
    }
  })
  .catch(err => {
    console.log("Analytics error:", err.response?.data || err.message);
  });
},

    fetchFollowersCount() {
      if (!this.selectedUserId) return;
      this.userStatsCards[0].loading = true;
      axios
        .get(`https://geotalent-backend-production.up.railway.app/api/UserProfile/LoadProfile?profileID=${this.selectedUserId}`, {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          if (res.data.status === "success") {
            this.userStatsCards[0].value = res.data.data.followersCount || 0;
          } else {
            this.userStatsCards[0].value = 0;
          }
        })
        .catch(() => {
          this.userStatsCards[0].value = 0;
        })
        .finally(() => {
          this.userStatsCards[0].loading = false;
        });
    },

    fetchUserInfo() {
      if (!this.selectedUserId) return;
      this.userStatsCards[1].loading = true;
      this.userStatsCards[2].loading = true;
      axios
        .get(`https://geotalent-backend-production.up.railway.app/api/Users/${this.selectedUserId}`, {
          headers: { Authorization: "Bearer " + this.$store.state.userData.token },
        })
        .then(res => {
          if (res.data.status === "success") {
            const user = res.data.data || {};
            this.userStatsCards[1].value = user.name || "N/A";
            this.userStatsCards[2].value = user.role || "N/A";
          }
        })
        .catch(() => {
          this.userStatsCards[1].value = "N/A";
          this.userStatsCards[2].value = "N/A";
        })
        .finally(() => {
          this.userStatsCards[1].loading = false;
          this.userStatsCards[2].loading = false;
        });
    },
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.fetchUsers();
    this.fetchTalents();
    this.fetchScouts();
    this.fetchAppointments();
    this.fetchPosts();
    this.fetchReels();
    this.fetchAdvertisements();
    this.fetchNews();
    this.fetchCommunities();
    this.fetchTotalFollowersAnalytics();
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>/* -------------------------
   Dashboard base styles
   ------------------------- */
.dashboard-title {
  font-weight: 700;
  color: #a5a1a1;
}
.dashboard-wrapper .hh {
  margin-bottom: 0px !important;
}

/* Card base */
.stats-card {
  height: 130px;
  border-radius: 14px;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 15px 10px;
  box-sizing: border-box;
}
.stats-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.15);
}

/* Blue gradient */
.stats-gradient {
  background: linear-gradient(118deg, #2953a2, rgba(41, 83, 162, 0.85));
  color: #fff !important;
}

/* Green */
.stats-green {
  background: linear-gradient(118deg, #4dc3b6, rgba(77, 195, 182, 0.85));
  color: #fff !important;
}

.card-title.stats-label {
  font-size: 0.85rem;
  font-weight: 600;
  opacity: 0.95;
  margin-bottom: 8px;
  text-align: center;
}

.stats-number {
  font-size: 2.2rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-border {
  min-height: 40px;
}

.appointments-breakdown {
  font-size: 0.85rem;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: #a5a1a1;
  border-left: 4px solid #4dc3b6;
  padding-left: 8px;
}

/* -------------------------
   Dropdown (theme-aware)
   ------------------------- */
.dropdown-root {
  position: relative;
  max-width: 720px;
  width: 100%;
  outline: none;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial;
}

/* Trigger */
.dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  color: var(--text-color, #212529);
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  transition: box-shadow .18s ease, border-color .18s ease, background .12s ease;
}
.dropdown-trigger:focus {
  outline: none;
  box-shadow: 0 0 0 4px var(--accent-color, #4dc3b6);
  border-color: var(--accent-color, #4dc3b6);
}
.dropdown-trigger.open {
  border-color: var(--accent-color, #4dc3b6);
}
.trigger-text {
  max-width: calc(100% - 80px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: inherit;
}
.trigger-icons {
  display: flex;
  align-items: center;
}
.clear-btn {
  margin-right: 8px;
  border: none;
  background: transparent;
  color: var(--muted, #6c757d);
  font-size: 0.95rem;
  cursor: pointer;
}
.clear-btn:hover {
  color: var(--text-color, #212529);
}
.chev {
  transition: transform 0.18s ease;
  color: var(--muted, #6c757d);
}
.chev.rotated {
  transform: rotate(180deg);
}

/* Panel */
.dropdown-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  border-radius: 10px;
  z-index: 1200;
  box-shadow: 0 10px 30px rgba(6, 8, 15, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Search area */
.dropdown-search-wrap {
  display: flex;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.08));
}
.dropdown-search {
  flex: 1;
  padding: 8px 10px;
  background: var(--input-bg, rgba(0,0,0,0.03));
  border: 1px solid var(--border-color, rgba(0,0,0,0.08));
  color: var(--text-color, #212529);
  border-radius: 6px;
  font-size: 0.95rem;
}
.search-count {
  margin-left: 8px;
  color: var(--muted, #6c757d);
  font-size: 0.85rem;
}

/* List */
.dropdown-list {
  max-height: 260px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: transparent;
}

/* Each item */
.dropdown-item {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.03));
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: background 0.14s ease, color 0.14s ease;
  background: transparent;
  color: var(--text-color, #212529);
}
.dropdown-item .item-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dropdown-item .item-name {
  font-weight: 600;
}
.dropdown-item .item-meta {
  font-size: 0.86rem;
  color: var(--muted, #6c757d);
}

/* Focus / hover state uses subtle accent */
.dropdown-item:hover,
.dropdown-item.focused {
  background: var(--hover-bg, rgba(77,195,182,0.06));
}

/* Empty state */
.dropdown-empty {
  padding: 14px;
  text-align: center;
  color: var(--muted, #6c757d);
}

/* Footer */
.dropdown-footer {
  padding: 8px 12px;
  border-top: 1px solid var(--border-color, rgba(0,0,0,0.08));
  display: flex;
  justify-content: flex-end;
}

/* -------------------------
   Transition & responsive
   ------------------------- */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.16s ease;
}
.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-6px) scale(0.995);
}
.dropdown-fade-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.dropdown-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.995);
}

/* extra helpful styles for making long file (no functional changes) */
.info-note {
  color: #9fb6f0;
  font-size: 0.9rem;
  margin-top: 6px;
}
.debug-block {
  display: none; /* set to block for debugging during development */
  background: rgba(0,0,0,0.4);
  padding: 8px;
  color: #fff;
  border-radius: 6px;
}
</style>
